<template>
    <div>
        <v-row no-gutters class="mt-2">
            <v-col cols="2" class="pr-2">
                <div style="max-width: 200px" class="d-flex flex-column">
                    <v-select-filled
                        v-model="selectedPartitionId"
                        :items="allPartitions"
                        item-value="id"
                        label="Partition"
                        outlined
                        v-if="allPartitions && allPartitions.length > 1"
                        @update:model-value="skipDetailQuery = false"
                        density="compact"
                        :item-title="(item) => item.name"
                    >
                    </v-select-filled>
                </div>
                <div class="d-flex flex-column pl-3">
                    <h4>Solde de début</h4>
                </div>
                <div class="d-flex flex-column pl-2">
                    <v-radio-group
                        v-model="balanceComputationMode"
                        inline
                        @change="skipChartQuery = false"
                        color="primary"
                    >
                        <v-radio label="Réel" value="ACTUAL" />
                        <v-radio label="Manuel" value="MANUAL" />
                    </v-radio-group>
                </div>
                <div class="d-flex flex-column">
                    <oss-text-field-filled
                        v-if="balanceComputationMode === 'MANUAL'"
                        hide-details
                        @change="skipChartQuery = false"
                        v-model="manualStartBalance"
                    />
                    <oss-text-field-filled
                        v-if="balanceComputationMode === 'ACTUAL'"
                        hide-details
                        disabled
                        v-model="startBalance"
                    />
                </div>
            </v-col>
            <!-- <v-row>
                <v-col cols="2">
                    <oss-date-picker-filled
                        v-bind:date="date"
                        v-bind:type="'month'"
                        v-bind:format="'MM/YYYY'"
                        :outlined="true"
                        @change-date="onDateChanged"
                        :textFieldReadOnly="false"
                        >
                    </oss-date-picker-filled>
                </v-col>
            </v-row> -->
            <v-col cols="10">
                <div :style="barChartStyle" v-if="chartData">
                    <LineChart
                        :data="chartData"
                        :options="options"
                        :plugins="plugins"
                        :height="200"
                        @chart:rendered="handleChartRender"
                        @chart:updated="handleChartRender"
                        :key="refreshKey"
                    />
                </div>
            </v-col>
        </v-row>
        <v-row
            no-gutters
            v-if="showSlider && chartData && chartData.labels && chartData.labels.length"
        >
            <v-col cols="2" class="pr-2">
                <!-- Mois de transition -->
            </v-col>
            <v-col cols="10" :style="sliderStyle">
                <v-slider
                    v-model="todayMonthIndice"
                    min="0"
                    :max="chartData.labels.length"
                    class="ma-0"
                    :style="{ width: `${sliderWidth}px` }"
                    :disabled="disableSlider"
                />
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col
                cols="12"
                class="mt-2"
                :style="
                    'padding-right: ' +
                    (this.tablePaddingRight + this.additionalPaddingRight) +
                    'px;'
                "
                v-if="detailTable && detailTable.length"
            >
                <v-data-table
                    :items="detailTable"
                    :headers="headers"
                    :items-per-page="-1"
                    hide-default-footer
                    v-if="detailTable"
                    :single-expand="false"
                    item-key="name"
                    show-expand
                    :loading="cashFlowPlanDetailLoading"
                    :key="refreshKey"
                    density="compact"
                    class="table"
                >
                    <template #headers="{ columns: headers }">
                        <tr>
                            <template v-for="(header, index) in headers">
                                <th
                                    v-if="!!header.align"
                                    role="columnheader"
                                    :class="`text-${header.align}`"
                                    :aria-label="header.title"
                                    scope="col"
                                    @click="onTableHeaderColumnClick(index)"
                                    :style="headerStyle(index)"
                                    v-tooltip:bottom="{
                                        text: 'Voir la répartition du mois',
                                        disabled: !isMonthColumn(index),
                                    }"
                                >
                                    <span>{{ header.title }}</span>
                                </th>
                            </template>
                        </tr>
                    </template>
                    <template v-slot:no-data>Aucune données</template>
                    <template v-slot:item="props">
                        <tr @click="expand(props.item)">
                            <td class="text-left" :style="'padding:0 0 0 12px; width:32px'">
                                <template v-if="props.item.subRows && props.item.subRows.length">
                                    <v-icon v-if="props.item.expanded === false" size="x-small"
                                        >$keyboard_arrow_down</v-icon
                                    >
                                    <v-icon v-else size="x-small">$keyboard_arrow_up</v-icon>
                                </template>
                            </td>
                            <td class="text-left">
                                <strong>{{ getRowName(props.item) }}</strong>
                            </td>
                            <td
                                v-for="(value, mi) in props.item.cashFlow"
                                :key="props.index + '-' + mi"
                                :class="amountClass(props.item, mi)"
                                :style="amountStyle(props.item, mi)"
                                @click.stop="openEditCell(props.item, mi)"
                            >
                                {{ getValue(props.item, mi) }}
                            </td>
                            <td
                                class="text-center"
                                :style="amountStyle(props.item, props.item.cashFlow.length)"
                                v-if="additionalColCount"
                            >
                                <span v-if="getBalanceDelta(props.item)">{{
                                    newCommonFormatCurrency(getBalanceDelta(props.item))
                                }}</span>
                                <v-icon v-else size="small">$oss-circle-check</v-icon>
                            </td>
                            <td
                                class="text-center"
                                :style="amountStyle(props.item, props.item.cashFlow.length)"
                                v-if="additionalColCount"
                            >
                                {{ newCommonFormatCurrency(props.item.balance) }}
                            </td>
                        </tr>
                        <template
                            v-if="
                                props.item.subRows &&
                                props.item.subRows.length &&
                                props.item.expanded
                            "
                        >
                            <template
                                v-for="(subRow, index) in props.item.subRows"
                                :key="props.index + '-' + subRow.name + '-' + index"
                            >
                                <tr @click="expand(subRow)">
                                    <td :style="'padding:0 0 0 12px; width:32px'">
                                        <template v-if="subRow.subRows && subRow.subRows.length">
                                            <v-icon v-if="subRow.expanded === false" size="x-small"
                                                >$keyboard_arrow_down</v-icon
                                            >
                                            <v-icon v-else size="x-small"
                                                >$keyboard_arrow_up</v-icon
                                            >
                                        </template>
                                    </td>
                                    <td class="text-left">
                                        <strong class="ml-2">{{ getRowName(subRow) }}</strong>
                                    </td>
                                    <td
                                        v-for="(value, index) in subRow.cashFlow"
                                        :key="index"
                                        :class="amountClass(subRow, index)"
                                        :style="amountStyle(subRow, index)"
                                        @click.stop="openEditCell(subRow, index)"
                                    >
                                        {{ getValue(subRow, index) }}
                                    </td>
                                    <td
                                        class="text-center"
                                        :style="amountStyle(subRow, subRow.cashFlow.length)"
                                        v-if="additionalColCount"
                                    >
                                        <span v-if="getBalanceDelta(subRow)">{{
                                            newCommonFormatCurrency(getBalanceDelta(subRow))
                                        }}</span>
                                        <v-icon v-else size="small">$oss-circle-check</v-icon>
                                    </td>
                                    <td
                                        class="text-center"
                                        :style="amountStyle(subRow, subRow.cashFlow.length)"
                                        v-if="additionalColCount"
                                    >
                                        {{ newCommonFormatCurrency(subRow.balance) }}
                                    </td>
                                </tr>
                                <template
                                    v-if="
                                        subRow.subRows && subRow.subRows.length && subRow.expanded
                                    "
                                >
                                    <tr
                                        v-for="(subSubRow, index2) in subRow.subRows"
                                        :key="
                                            props.index +
                                            '-' +
                                            subRow.name +
                                            index +
                                            '-' +
                                            subSubRow.name +
                                            '-' +
                                            index2
                                        "
                                    >
                                        <td :style="'padding:0 0 0 12px; width:32px'" />
                                        <td class="text-left">
                                            <strong class="ml-2">{{
                                                getRowName(subSubRow)
                                            }}</strong>
                                        </td>
                                        <td
                                            v-for="(value, index) in subSubRow.cashFlow"
                                            :key="index"
                                            :class="amountClass(subSubRow, index)"
                                            :style="amountStyle(subSubRow, index)"
                                            @click.stop="openEditCell(subSubRow, index)"
                                        >
                                            {{ getValue(subSubRow, index) }}
                                        </td>
                                        <td
                                            class="text-center"
                                            :style="
                                                amountStyle(subSubRow, subSubRow.cashFlow.length)
                                            "
                                            v-if="additionalColCount"
                                        >
                                            <span v-if="getBalanceDelta(subRow)">{{
                                                newCommonFormatCurrency(getBalanceDelta(subSubRow))
                                            }}</span>
                                            <v-icon v-else size="small">$oss-circle-check</v-icon>
                                        </td>
                                        <td
                                            class="text-center"
                                            :style="
                                                amountStyle(
                                                    subSubRow,
                                                    subSubRow.cashFlow.length + 1,
                                                )
                                            "
                                            v-if="additionalColCount"
                                        >
                                            {{ newCommonFormatCurrency(subSubRow.balance) }}
                                        </td>
                                    </tr>
                                </template>
                            </template>
                        </template>
                    </template>
                    <template v-slot:body.append>
                        <tr>
                            <td :style="'padding:0 0 0 12px; width:32px'" />
                            <td class="text-left">
                                <strong class="ml-2">Total</strong>
                            </td>
                            <td v-for="(value, mi) in totals" :key="mi">
                                {{ newCommonFormatCurrency(value) }}
                            </td>
                            <td class="text-center" v-if="additionalColCount" />
                            <td class="text-center" v-if="additionalColCount" />
                        </tr>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
        <oss-dialog
            v-model="showEditCellDialog"
            title="Paiement"
            :validDisabled="!isEditionValid"
            @valid="saveEditCell()"
            :showValid="true"
            v-if="showEditCellDialog"
            max-width="30wh"
        >
            <div style="min-width: 30vw">
                <v-form v-model="isEditionValid">
                    <v-row no-gutters v-if="!disableInLateAmountEdition">
                        <v-col cols="12">
                            Reste à payer :
                            {{
                                newCommonFormatCurrency(cellToEdit.row.balance - totalInLateAmount)
                            }}
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col cols="12">
                            <oss-text-field-filled
                                label="budgeté"
                                class="mt-2"
                                autofocus
                                :readonly="!!inBudgetId"
                                v-model="cellToEdit.budgetAmount"
                            />
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col cols="12">
                            <oss-text-field-filled
                                label="En retard"
                                class="mt-2"
                                autofocus
                                :disabled="disableInLateAmountEdition"
                                :rules="inLateAmountRules"
                                v-model="cellToEdit.inLateAmount"
                            />
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col cols="12" class="d-flex justify-end mt-2">
                            <v-btn
                                v-if="ifCellToEditAlreadyModified"
                                @click.stop="resetCustomAmount"
                                size="x-small"
                            >
                                Réinitialiser la valeur
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </div>
        </oss-dialog>
        <v-snackbar v-model="monthDataSnackbar" color="warning" multi-line>
            <span>{{ monthDataSnackbarMsg }}</span>
            <template v-slot:actions>
                <v-btn color="black" @click="monthDataSnackbar = false" variant="text">
                    Fermer
                </v-btn>
            </template>
        </v-snackbar>
        <oss-dialog
            v-model="distributionDialog"
            :title="`Répartition mensuelle`"
            :fullscreen="$vuetify.display.mobile"
            v-if="distributionDialog"
        >
            <v-row no-gutters>
                <v-col cols="8" class="d-flex flex-row justify-start align-center">
                    Période:
                    {{
                        `${selectedMonthsInRangeNames[0]} ${
                            selectedMonthsInRangeNames.length > 1
                                ? "à " +
                                  selectedMonthsInRangeNames[selectedMonthsInRangeNames.length - 1]
                                : ""
                        }`
                    }}

                    <v-btn-icon
                        icon="$fa-calendar-alt"
                        size="small"
                        class="mx-3"
                        color="#777"
                        @click.stop="toggleDatePicker = toggleDatePicker === 1 ? 0 : 1"
                        v-tooltip:top="'Sélectionner une autre période'"
                    />
                </v-col>
                <v-col cols="4" class="d-flex flex-row justify-end">
                    <v-btn-toggle
                        v-model="toggleMonthDataViewType"
                        rounded
                        mandatory
                        density="compact"
                    >
                        <v-btn :disabled="toggleDatePicker === 1"> Tableau </v-btn>
                        <v-btn :disabled="toggleDatePicker === 1"> Graphique </v-btn>
                    </v-btn-toggle>
                </v-col>
                <v-col cols="12">
                    <v-window v-model="toggleDatePicker">
                        <v-window-item :value="0">
                            <v-window v-model="toggleMonthDataViewType">
                                <v-window-item :value="0">
                                    <v-row no-gutters>
                                        <v-col cols="12">
                                            <v-table
                                                fixed-header
                                                density="compact"
                                                v-if="selectedMonthsRangeData"
                                                height="60vh"
                                            >
                                                <template v-slot:default>
                                                    <thead>
                                                        <template
                                                            v-if="
                                                                selectedMonthsRangeData[0]
                                                                    .monthValues.length === 1
                                                            "
                                                        >
                                                            <tr>
                                                                <th>Etiquette</th>
                                                                <th>Réel</th>
                                                                <th>Répartition</th>
                                                                <th>Estimé</th>
                                                                <th>Répartition budget</th>
                                                                <th>Variation</th>
                                                            </tr>
                                                        </template>
                                                        <template v-else>
                                                            <tr>
                                                                <th>
                                                                    <strong>Etiquette</strong>
                                                                </th>
                                                                <th
                                                                    v-for="monthName of selectedMonthsInRangeNames"
                                                                    :key="monthName"
                                                                >
                                                                    <strong>{{ monthName }}</strong>
                                                                </th>
                                                                <th><strong>Total</strong></th>
                                                                <th><strong>Estimé</strong></th>
                                                            </tr>
                                                        </template>
                                                    </thead>
                                                    <tbody>
                                                        <template
                                                            v-if="
                                                                selectedMonthsRangeData[0]
                                                                    .monthValues.length === 1
                                                            "
                                                        >
                                                            <tr
                                                                v-for="(
                                                                    lineData, lineIndex
                                                                ) of selectedMonthsRangeData"
                                                                :key="lineIndex"
                                                            >
                                                                <td>
                                                                    <strong>{{
                                                                        lineData.name
                                                                    }}</strong>
                                                                </td>
                                                                <td>
                                                                    {{
                                                                        newCommonFormatCurrency(
                                                                            lineData.monthValues[0]
                                                                                .amount,
                                                                        )
                                                                    }}
                                                                </td>
                                                                <td
                                                                    v-if="
                                                                        lineData.monthValues[0]
                                                                            .relativeAmountPercent ==
                                                                        null
                                                                    "
                                                                >
                                                                    -
                                                                </td>
                                                                <td v-else>
                                                                    {{
                                                                        roundNumber(
                                                                            lineData.monthValues[0]
                                                                                .relativeAmountPercent,
                                                                        )
                                                                    }}%
                                                                </td>
                                                                <td>
                                                                    {{
                                                                        newCommonFormatCurrency(
                                                                            lineData.monthValues[0]
                                                                                .budgetAmount,
                                                                        )
                                                                    }}
                                                                </td>
                                                                <td
                                                                    v-if="
                                                                        lineData.monthValues[0]
                                                                            .relativeBudgetPercent ==
                                                                        null
                                                                    "
                                                                >
                                                                    -
                                                                </td>
                                                                <td v-else>
                                                                    {{
                                                                        roundNumber(
                                                                            lineData.monthValues[0]
                                                                                .relativeBudgetPercent,
                                                                        )
                                                                    }}%
                                                                </td>
                                                                <td
                                                                    :class="{
                                                                        green:
                                                                            lineData.monthValues[0]
                                                                                .diff > 0,
                                                                        'custom-orange':
                                                                            lineData.monthValues[0]
                                                                                .diff < 0,
                                                                    }"
                                                                >
                                                                    {{
                                                                        newCommonFormatCurrency(
                                                                            lineData.monthValues[0]
                                                                                .diff,
                                                                        )
                                                                    }}
                                                                </td>
                                                            </tr>
                                                        </template>
                                                        <template v-else>
                                                            <tr
                                                                v-for="(
                                                                    rowData, rowIndex
                                                                ) of selectedMonthsRangeData"
                                                                :key="rowIndex"
                                                            >
                                                                <td>
                                                                    <strong>{{
                                                                        rowData.name
                                                                    }}</strong>
                                                                </td>
                                                                <td
                                                                    v-for="(
                                                                        monthData, index
                                                                    ) of rowData.monthValues"
                                                                    :key="index"
                                                                >
                                                                    {{
                                                                        newCommonFormatCurrency(
                                                                            monthData.amount,
                                                                        )
                                                                    }}
                                                                </td>
                                                                <td>
                                                                    <strong>{{
                                                                        newCommonFormatCurrency(
                                                                            sumByKey(
                                                                                rowData.monthValues,
                                                                                "amount",
                                                                            ),
                                                                        )
                                                                    }}</strong>
                                                                </td>
                                                                <td>
                                                                    <strong>{{
                                                                        newCommonFormatCurrency(
                                                                            sumByKey(
                                                                                rowData.monthValues,
                                                                                "budgetAmount",
                                                                            ),
                                                                        )
                                                                    }}</strong>
                                                                </td>
                                                            </tr>
                                                        </template>
                                                    </tbody>
                                                    <tfoot>
                                                        <template
                                                            v-if="
                                                                selectedMonthsRangeData[0]
                                                                    .monthValues.length === 1
                                                            "
                                                        >
                                                            <tr
                                                                v-for="(
                                                                    lineData, lineIndex
                                                                ) of selectedMonthsRangeTotalsData"
                                                                :key="lineIndex"
                                                            >
                                                                <td>
                                                                    <strong>{{
                                                                        lineData.name
                                                                    }}</strong>
                                                                </td>
                                                                <td>
                                                                    <strong>{{
                                                                        newCommonFormatCurrency(
                                                                            lineData.monthValues[0]
                                                                                .amount,
                                                                        )
                                                                    }}</strong>
                                                                </td>
                                                                <td>-</td>
                                                                <td>
                                                                    <strong>{{
                                                                        newCommonFormatCurrency(
                                                                            lineData.monthValues[0]
                                                                                .budgetAmount,
                                                                        )
                                                                    }}</strong>
                                                                </td>
                                                                <td>-</td>
                                                                <td
                                                                    :class="{
                                                                        green:
                                                                            lineData.monthValues[0]
                                                                                .diff > 0,
                                                                        'custom-orange':
                                                                            lineData.monthValues[0]
                                                                                .diff < 0,
                                                                    }"
                                                                >
                                                                    <strong>{{
                                                                        newCommonFormatCurrency(
                                                                            lineData.monthValues[0]
                                                                                .diff,
                                                                        )
                                                                    }}</strong>
                                                                </td>
                                                            </tr>
                                                        </template>
                                                        <template v-else>
                                                            <tr
                                                                v-for="(
                                                                    lineData, lineIndex
                                                                ) of selectedMonthsRangeTotalsData"
                                                                :key="lineIndex"
                                                            >
                                                                <td>
                                                                    <strong>{{
                                                                        lineData.name
                                                                    }}</strong>
                                                                </td>
                                                                <td
                                                                    v-for="(
                                                                        monthData, index
                                                                    ) of lineData.monthValues"
                                                                    :key="index"
                                                                >
                                                                    <strong>{{
                                                                        newCommonFormatCurrency(
                                                                            monthData.amount,
                                                                        )
                                                                    }}</strong>
                                                                </td>
                                                                <td>
                                                                    <strong>{{
                                                                        newCommonFormatCurrency(
                                                                            sumByKey(
                                                                                lineData.monthValues,
                                                                                "amount",
                                                                            ),
                                                                        )
                                                                    }}</strong>
                                                                </td>
                                                                <td>
                                                                    <strong>{{
                                                                        newCommonFormatCurrency(
                                                                            sumByKey(
                                                                                lineData.monthValues,
                                                                                "budgetAmount",
                                                                            ),
                                                                        )
                                                                    }}</strong>
                                                                </td>
                                                            </tr>
                                                        </template>
                                                    </tfoot>
                                                </template>
                                            </v-table>
                                        </v-col>
                                    </v-row>
                                </v-window-item>
                                <v-window-item :value="1">
                                    <v-row no-gutters>
                                        <v-col cols="12" class="d-flex">
                                            <v-switch
                                                v-model="pieChartSwitchValue"
                                                :label="'Budget'"
                                                class="ml-auto"
                                            />
                                        </v-col>
                                        <v-col
                                            cols="12"
                                            v-if="
                                                (!selectedMonthsHaveDataFor('amount') &&
                                                    !pieChartSwitchValue) ||
                                                (!selectedMonthsHaveDataFor('budgetAmount') &&
                                                    pieChartSwitchValue)
                                            "
                                            :style="{
                                                minHeight: $vuetify.display.mobile
                                                    ? '200px'
                                                    : '300px',
                                            }"
                                            class="d-flex justify-center align-center"
                                        >
                                            <span class="text-center">
                                                Pas assez de données pour afficher le graphique de
                                                la répartion
                                                {{ pieChartSwitchValue ? "budgétaire" : "réelle" }}.
                                            </span>
                                        </v-col>
                                        <v-col
                                            cols="12"
                                            v-show="
                                                (selectedMonthsHaveDataFor('amount') &&
                                                    !pieChartSwitchValue) ||
                                                (selectedMonthsHaveDataFor('budgetAmount') &&
                                                    pieChartSwitchValue)
                                            "
                                        >
                                            <pie-chart
                                                :data="selectedMonthsPieChartData"
                                                :options="pieChartOptions"
                                                :height="$vuetify.display.mobile ? 200 : 300"
                                                style="max-height: 400px"
                                                :key="pieChartRefreshKey"
                                            />
                                            <h4 class="text-center pt-4">
                                                {{
                                                    `Répartition ${
                                                        pieChartSwitchValue
                                                            ? "budgétaire"
                                                            : "réelle"
                                                    } mensuelle : ${
                                                        selectedMonthsInRangeNames[0]
                                                    } ${
                                                        selectedMonthsInRangeNames.length > 1
                                                            ? "à " +
                                                              selectedMonthsInRangeNames[
                                                                  selectedMonthsInRangeNames.length -
                                                                      1
                                                              ]
                                                            : ""
                                                    }`
                                                }}
                                            </h4>
                                        </v-col>
                                    </v-row>
                                </v-window-item>
                            </v-window>
                        </v-window-item>
                        <v-window-item :value="1">
                            <v-row no-gutters class="py-3">
                                <v-col cols="12" class="d-flex justify-center"
                                    >Sélectionner une période</v-col
                                >
                                <v-col cols="12" class="d-flex flex-row justify-center">
                                    <!-- <MonthPicker
                                        v-model="selectedMonths"
                                        color="gradient"
                                        rounded="lg"
                                        :border="false"
                                        range
                                        :min="minDatePickerValue"
                                        :max="maxDatePickerValue"
                                        :landscape="!$vuetify.display.mobile"
                                    ></MonthPicker> -->
                                    <v-date-picker-wrapper
                                        v-model="selectedMonths"
                                        color="gradient"
                                        first-day-of-week="1"
                                        type="month"
                                        style="max-width: 800px"
                                        scrollable
                                        range
                                        full-width
                                        hide-header
                                        :landscape="!$vuetify.display.mobile"
                                        :min="minDatePickerValue"
                                        :max="maxDatePickerValue"
                                    />
                                </v-col>
                                <v-col cols="12" class="pt-3 d-flex justify-end">
                                    <v-btn @click.stop="toggleDatePicker = 0" size="small">
                                        <span>Valider</span>
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-window-item>
                    </v-window>
                </v-col>
            </v-row>
        </oss-dialog>
    </div>
</template>

<script lang="ts">
import moment from "moment-timezone";
import _ from "lodash";
import { Apollo } from "@/decorators";
import { Line as LineChart, Pie as PieChart } from "vue-chartjs";

import CASHFLOW_PLAN from "../../graphql/budget/CashFlowPlan.gql";
import CASHFLOW_PLAN_DETAIL from "../../graphql/budget/CashFlowPlanDetail.gql";
import PARTITION_TAG_QUERY from "../../graphql/tag/CompanyPartitionTags.gql";
// import ACCOUNTABLE_ACCOUNT_PARTITION_TAGS from '../../graphql/tag/AccountableAccountPartitionTags.gql';

import BUDGET_QUERY from "../../graphql/budget/Budget.gql";
import BUDGET_MANUAL_QUERY from "../../graphql/budget/BudgetManual.gql";
import OPERATING_ACCOUNT_QUERY from "../../graphql/tag/OperatingAccounts.gql";

import { Chart } from "chart.js";

import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import { CashFlowPlanDetailFront } from "./CashFlowPlanDetailFront";
import { getDBFormattedEndDate, getDBFormattedStartDate } from "@/services/dateServices";
import { chartColors } from "../../../../backend/src/common/chart/graphServices";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";

import MonthPicker from "../../components/ui/month-picker/MonthPicker.vue";

let gChart = null;

@Component({
    components: {
        LineChart,
        PieChart,
        MonthPicker,
    },
    emits: ["periods-list-changed"],
})
export class CashFlowPlan extends Vue {
    @Prop({ default: "" }) companyId: string;
    @Prop({ default: "" }) inBudgetId: string;
    @Prop({ default: "" }) startDate: string;
    @Prop({ default: "" }) endDate: string;
    @Prop({ default: 1 }) selectedPeriod: number;
    @Prop({ default: 0 }) refreshData: number;

    selectedPartitionId = "Without";

    distributionDialog = false;
    pieChartSwitchValue = false;
    columnsOffsetBeforeMonths = 2; // Number of columns in the table before the first month. This offsets the index of the first month. Update it the table changes
    sliderWidth = 500;
    toggleMonthDataViewType = 0;
    monthDataSnackbar = false;
    monthDataSnackbarMsg = "";
    selectedMonths_ = [];
    toggleDatePicker = 0;

    // aaPartitionTags = []

    color = {
        r: 80,
        g: 143,
        b: 172,
    };
    // "rgba(249,177,42,0.2)"

    // date:string = moment().startOf("years").format("YYYY-MM-DD")

    todayBackgroundColor = "#DFDFDF";

    //
    // floatingIncomes = ref([])
    // floatingExpenses = ref([])
    // cashFlow = ref([])
    // budgetCashFlow = ref([])
    floatingIncomes = [];
    floatingExpenses = [];
    cashFlow = [];
    budgetCashFlow = [];

    startDate_ = null;
    endDate_ = null;
    periodsList = [];
    skipDetailQuery: boolean = true;
    skipChartQuery: boolean = true;
    disableSlider: boolean = false;

    balanceComputationMode: string = "ACTUAL";
    manualStartBalance: number = 0;
    startBalance: number = 0;

    slided = false;

    refreshKey = 0;

    // get enableEdition() {
    //     if (!this.budget) return true
    //     if (this.budget.type === "MANUAL") return true
    //     return false
    // }

    get selectedMonths() {
        if (!this.selectedMonths_.length && this.allPossibleDateMonths) {
            this.selectedMonths_.push(this.allPossibleDateMonths[0]);
        }
        return this.selectedMonths_;
    }

    get minDatePickerValue() {
        return this.allPossibleDateMonths?.[0];
    }

    get maxDatePickerValue() {
        const len = this.allPossibleDateMonths?.length;
        return len ? this.allPossibleDateMonths[len - 1] : null;
    }

    // @Watch("selectedMonths")
    // watchSelectedMonths(val) {
    //     console.log("selectedMonths", this.selectedMonths)
    // }

    set selectedMonths(newVal) {
        this.selectedMonths_ = [...newVal];
    }

    get showSlider() {
        return this.inBudgetId;
    }

    get additionalColCount(): number {
        // return this.enableEdition ? 2 : 0
        return 2;
    }

    get additionalColWidth() {
        return 90;
    }

    get paddingRight() {
        return this.additionalColCount
            ? this.additionalColCount * this.additionalColWidth
            : 0.5 * this.additionalColWidth;
    }

    get barChartStyle() {
        return (
            "background-color: #FFF; border-radius: 16px !important; padding-right: " +
            this.paddingRight +
            "px;"
        );
    }

    get tablePaddingRight() {
        return this.additionalColCount ? 0 : 0.5 * this.additionalColWidth;
    }

    get additionalPaddingRight() {
        return this.showSlider ? 8 : 0;
    }

    get budgetId() {
        // if (this.cashFlowPlanDetail?.budgetId) {
        //     return this.cashFlowPlanDetail.budgetId
        // }
        if (this.budget?.id) {
            return this.budget.id;
        }
        return this.inBudgetId;
    }

    newCommonFormatCurrency(value) {
        return newCommonFormatCurrency(value, undefined);
    }

    async beforeMount() {
        this.initialize();
        this.updateCashFlow();

        this.computeLimitDates();
        this.emitPeriodsList();
    }

    get defaultPartition() {
        return {
            id: "Without",
            name: "Affectation comptable",
            type: "",
        };
    }

    get allPartitions() {
        const pts = [this.defaultPartition];
        // if (this.aaPartitionTags) {
        //     const defautAAPT = this.aaPartitionTags.find(pt => pt.name === "default")
        //     if (defautAAPT) {
        //         pts.push({
        //             id: defautAAPT.id,
        //             name: "Standard",
        //             type:  "partitionTag"
        //         })
        //     }
        // }
        if (this.partitionTags) {
            for (const pt of this.partitionTags) {
                pts.push({
                    id: pt.id,
                    name: pt.name,
                    type: "partitionTag",
                });
            }
        }
        if (this.operatingAccounts) {
            for (const operatingAccount of this.operatingAccounts) {
                pts.push({
                    id: operatingAccount.id,
                    name: operatingAccount.name,
                    type: "operatingAccount",
                });
            }
        }
        return pts;
    }

    get selectedPartition() {
        if (this.allPartitions) {
            const selectedPartition = this.allPartitions.find(
                (pt) => pt.id === this.selectedPartitionId,
            );
            if (selectedPartition) {
                return selectedPartition;
            }
        }
        return this.defaultPartition;
    }

    @Watch("todayMonthIndice")
    onTodayMonthIndiceChanged() {
        this.updateCashFlow();
    }

    @Watch("refreshData")
    onRefreshDataChanged() {
        this.skipDetailQuery = false;
        this.skipChartQuery = false;
    }

    // @Watch("gChart")
    // onLoadingChartChanged() {
    //     this.sliderStyle;
    // }

    budgetQueryDone: boolean = false;

    @Apollo({
        query() {
            return BUDGET_QUERY;
        },
        variables() {
            return {
                id: this.inBudgetId,
            };
        },
        skip() {
            return !this.companyId || !this.inBudgetId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (data?.budget) {
                this.budgetFromId = data.budget;
                this.partitionTags = this.budgetFromId?.partitionTag
                    ? [this.budgetFromId?.partitionTag]
                    : [];
            } else {
                this.budgetFromId = null;
                this.partitionTags = [];
            }
            this.onBudgetChanged();
            this.budgetQueryDone = true;
        },
    })
    budgetFromId = null;

    @Apollo({
        query() {
            return BUDGET_MANUAL_QUERY;
        },
        variables() {
            return {
                companyId: this.companyId,
            };
        },
        skip() {
            return !this.companyId || this.inBudgetId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (data?.budgetFirst) {
                this.budgetManual = data.budgetFirst;
                this.partitionTags = this.budgetManual?.partitionTags || [];
            } else {
                this.budgetManual = null;
                this.partitionTags = [];
            }
            this.onBudgetChanged();
            this.budgetQueryDone = true;
        },
    })
    budgetManual = null;

    onBudgetChanged() {
        this.computeLimitDates();
        this.emitPeriodsList();
        this.initialize();
    }

    get budget() {
        if (this.inBudgetId) return this.budgetFromId;
        return this.budgetManual;
    }

    @Apollo({
        query() {
            return PARTITION_TAG_QUERY;
        },
        variables() {
            return {
                companyId: this.companyId,
            };
        },
        debounce: 500,
        skip() {
            return !this.companyId || this.inBudgetId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading && data) {
                this.partitionTags = data.partitionTags;
            } else {
                this.partitionTags = [];
            }
        },
    })
    partitionTags = [];

    // @Apollo({
    //     query: BUDGET_PARTITION_TAG,
    //     variables() {
    //         return {
    //             budgetId: this.budgetId
    //         }
    //     },
    //     skip() {
    //         return !this.budgetId
    //     },
    //     specificResult({ data, loading, networkStatus }: any) {
    //         if (!loading) {
    //             if (data?.budget) {
    //                 this.budget = data.budget
    //                 if(data.budget.partitionTag) {
    //                     this.partitionTags = [data.budget.partitionTag];
    //                 } else {
    //                     this.partitionTags = [];
    //                 }
    //             } else {
    //                 this.budget = null
    //                 this.partitionTags = [];
    //             }
    //         }
    //     }
    // })
    // budget = null

    @Apollo({
        query() {
            return OPERATING_ACCOUNT_QUERY;
        },
        variables() {
            return {
                companyId: this.companyId,
                use: "CASH_FLOW_PLAN",
            };
        },
        debounce: 500,
        skip() {
            return !this.companyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading && data && data.scopingOperatingAccount) {
                this.operatingAccounts = data.scopingOperatingAccount.data || [];
                // if (this.operatingAccounts.length) {
                //     this.selectorOperatingAccountJson = this.operatingAccounts[0].id
                // } else {
                //     this.selectorOperatingAccountJson = ""
                // }
            } else {
                this.operatingAccounts = [];
                // this.selectorOperatingAccountJson = ""
            }
        },
    })
    operatingAccounts = [];
    // selectorOperatingAccountJson = ""

    @Apollo({
        query: CASHFLOW_PLAN,
        variables() {
            return {
                budgetId: this.budgetId,
                companyId: this.companyId,
                startDate: this.startDate_,
                endDate: this.endDate_,
                balanceComputationMode: this.balanceComputationMode,
                manualStartBalance: this.manualStartBalance,
            };
        },
        skip() {
            return !this.companyId || this.skipChartQuery || !this.budgetQueryDone;
        },
        specificResult({ data, loading, networkStatus }: any) {
            // debugger
            if (!loading) {
                this.initialize();
                if (data && data.cashFlowPlan) {
                    this.cashFlowPlanData = data.cashFlowPlan.result;
                    if (this.balanceComputationMode === "ACTUAL") {
                        this.startBalance = data.cashFlowPlan.result.cashFlow.startBalance;
                    }
                    // this.skipChartQuery = true;
                } else {
                    this.cashFlowPlanData = null;
                    this.startBalance = null;
                }
                this.updateCashFlow();
            }
        },
    })
    cashFlowPlanData: any = null;

    refresh() {
        if (this.$apollo?.queries?.cashFlowPlanData) {
            this.$apollo.queries.cashFlowPlanData.refetch();
        } else {
            debugger;
        }
        if (this.$apollo?.queries?.cashFlowPlanDetail) {
            this.$apollo.queries.cashFlowPlanDetail.refetch();
        } else {
            debugger;
        }
    }

    @Apollo({
        query: CASHFLOW_PLAN_DETAIL,
        variables() {
            return {
                budgetId: this.budgetId,
                companyId: this.companyId,
                startDate: getDBFormattedStartDate(this.startDate_),
                endDate: getDBFormattedEndDate(this.endDate_),
                partitionTagId:
                    this.selectedPartitionId !== "Without" &&
                    this.selectedPartition.type === "partitionTag"
                        ? this.selectedPartitionId
                        : undefined,
                operatingAccountId:
                    this.selectedPartitionId !== "Without" &&
                    this.selectedPartition.type === "operatingAccount"
                        ? this.selectedPartitionId
                        : undefined,
            };
        },
        skip() {
            return (
                !this.companyId ||
                !this.cashFlowPlanData ||
                this.skipDetailQuery ||
                !this.budgetQueryDone
            );
        },
        specificResult({ data, loading, networkStatus }: any) {
            // debugger
            if (!loading) {
                if (data && data.cashFlowPlanDetail) {
                    const cashFlowPlanDetail = data.cashFlowPlanDetail.result;

                    this.cashFlowPlanDetail = new CashFlowPlanDetailFront(
                        this.$apollo,
                        this.companyId,
                        this.budgetId,
                        cashFlowPlanDetail,
                    );
                    this.cashFlowPlanDetail.initialize();
                    this.cashFlowPlanDetail.build();
                    this.onMonthsChanged();
                    // this.cashFlowPlanDetail.todayMonthIndice = this.todayMonthIndice
                    // this.colWidth = 0
                    // this.skipDetailQuery = true;
                } else {
                    this.cashFlowPlanDetail = null;
                }
            }
        },
    })
    cashFlowPlanDetail: CashFlowPlanDetailFront = null;

    get cashFlowPlanDetailLoading() {
        if (this.$apollo.queries.cashFlowPlanDetail) {
            return this.$apollo.queries.cashFlowPlanDetail.loading;
        }
        return false;
    }

    get incomes() {
        const incomes = [];
        if (this.cashFlowPlanData) {
            const cashFlow = this.cashFlowPlanData.cashFlow;
            const todayMonthIndice = this.nowIsInsidePeriod
                ? this.todayMonthIndice
                : this.months.length;
            for (let i = 0; i < todayMonthIndice; i++) {
                incomes.push(cashFlow.incomes[i]);
            }
            for (let i = todayMonthIndice; i < this.months.length; i++) {
                incomes.push(NaN);
            }
        }
        return incomes;
    }

    get budgetIncomes() {
        const incomes = [];
        if (this.cashFlowPlanData) {
            const budget = this.cashFlowPlanData.budget;
            const todayMonthIndice = this.nowIsInsidePeriod
                ? this.todayMonthIndice
                : this.months.length;
            for (let i = 0; i < todayMonthIndice; i++) {
                incomes.push(NaN);
            }
            // if (this.cashFlowPlanDetail?.budgets) {
            //     const budgets = this.cashFlowPlanDetail?.budgets
            //     for (let i = todayMonthIndice;i < this.months.length; i++) {
            //         incomes.push((budgets?.length && budgets[i] > 0) ? budgets[i] : 0)
            //     }
            // } else
            if (budget?.incomes) {
                const budget = this.cashFlowPlanData.budget;
                for (let i = todayMonthIndice; i < this.months.length; i++) {
                    incomes.push(budget?.incomes.length ? budget?.incomes[i] : 0);
                }
            }
        }
        return incomes;
    }

    get expenses() {
        const expenses = [];
        if (this.cashFlowPlanData) {
            const cashFlow = this.cashFlowPlanData.cashFlow;
            const todayMonthIndice = this.nowIsInsidePeriod
                ? this.todayMonthIndice
                : this.months.length;
            for (let i = 0; i < todayMonthIndice; i++) {
                expenses.push(cashFlow.expenses[i]);
            }
            for (let i = todayMonthIndice; i < this.months.length; i++) {
                expenses.push(NaN);
            }
        }
        return expenses;
    }

    get budgetExpenses() {
        const expenses = [];
        if (this.cashFlowPlanData) {
            const todayMonthIndice = this.nowIsInsidePeriod
                ? this.todayMonthIndice
                : this.months.length;
            for (let i = 0; i < todayMonthIndice; i++) {
                expenses.push(NaN);
            }
            // if (this.cashFlowPlanDetail?.budgets) {
            //     const budgets = this.cashFlowPlanDetail?.budgets
            //     for (let i = todayMonthIndice;i < this.months.length; i++) {
            //         expenses.push((budgets?.length && budgets[i] < 0) ? budgets[i] : 0)
            //     }
            // } else
            if (this.cashFlowPlanData.budget) {
                const budget = this.cashFlowPlanData.budget;
                for (let i = todayMonthIndice; i < this.months.length; i++) {
                    expenses.push(budget?.expenses?.length ? budget?.expenses[i] : 0);
                }
            }
        }
        return expenses;
    }

    get isChartDefined() {
        return !!gChart && this.sliderStyle;
    }

    initialize() {
        gChart = null;
    }

    onUpdateChart(chart) {
        if (this.sliderWidth !== chart.chartArea.width + 16) {
            this.sliderWidth = chart.chartArea.width + 16;
        }
        if (!gChart) {
            gChart = chart;
            this.computeAmountColWidth();
            this.computeSliderStyle();
        }
    }

    handleChartRender(chart: Chart<"line">) {
        console.log("handleChartRender");
        // console.log(chart)
        // if (chart?.chartArea?.width) {
        //     console.log("handleChartRender width", chart.chartArea.width);
        //     this.onUpdateChart(chart.chartArea.width)
        // } else {
        //     debugger
        // }
        // this.isChartDefined = false

        // this.isChartDefined = true;
        console.log("this.isChartDefined", this.isChartDefined);
        // if (!gChart) {
        //     gChart = chart
        // }
    }

    // handleChartUpdate(chart: Chart<'line'>) {
    //     console.log("handleChartUpdate")
    //     console.log(chart)
    //     if (chart?.chartArea?.width) {
    //         console.log("handleChartUpdate width", chart.chartArea.width);
    //         this.onUpdateChart(chart.chartArea.width)
    //     } else {
    //         debugger
    //     }
    // }

    get zeroPos() {
        let zeroPos = 0;
        if (this.isChartDefined && gChart && gChart.scales && gChart.scales.y) {
            const scaleY = gChart.scales.y;
            const ratio = (scaleY.bottom - scaleY.top) / (scaleY.min - scaleY.max);
            zeroPos = scaleY.top - ratio * scaleY.max;
        }
        return zeroPos;
    }

    get firstColWidth() {
        if (this.isChartDefined && gChart) {
            return gChart.chartArea.left;
        }
        return 0;
    }

    sliderStyle = "";

    computeSliderStyle() {
        if (gChart) {
            this.sliderStyle = `padding: 0 0 0 ${Math.round(
                gChart.chartArea.left +
                    this.amountColWidth * 0.5 -
                    this.additionalPaddingRight -
                    16,
            )}px;`;
        } else {
            this.sliderStyle = "";
        }
    }

    get headers() {
        const headers = [];
        headers.push({ title: "", value: "", sortable: false, align: "start" });
        headers.push({ title: "Titre", value: "title", sortable: false, align: "start" });
        if (this.chartData?.labels?.length) {
            for (const label of this.chartData.labels) {
                headers.push({ title: label, value: "", sortable: false, align: "center" });
            }
        }
        if (this.additionalColCount) {
            headers.push({ title: "Delta", value: "", sortable: false, align: "center" });
            headers.push({ title: "Solde compta", value: "", sortable: false, align: "center" });
        }
        console.log("headers =>", this.chartData?.labels?.length, " ", headers.length);

        return headers;
    }

    get plugin() {
        const plugin = {
            id: "custom_canvas_background_color",
            beforeDraw: (chart) => {
                if (!chart) {
                    debugger;
                    return;
                }
                if (this.nowIsInsidePeriod) {
                    const ctx = chart.canvas.getContext("2d");
                    ctx.save();
                    ctx.globalCompositeOperation = "destination-over";

                    const unitWidth = chart.chartArea.width / chart.data.labels.length;
                    const leftWidth = unitWidth * this.todayMonthIndice;
                    // ctx.fillStyle = 'lightGrey';
                    // ctx.fillRect(chart.chartArea.left, chart.chartArea.top, leftWidth, chart.chartArea.height);
                    ctx.fillStyle = this.todayBackgroundColor;
                    ctx.fillRect(
                        chart.chartArea.left + leftWidth,
                        chart.chartArea.top,
                        unitWidth,
                        chart.chartArea.height,
                    );

                    ctx.restore();
                }
            },
            afterDatasetDraw: (chart) => {
                if (!chart._metasets?.length) {
                    debugger;
                    return;
                }
                if (!chart._metasets[0].data.length) {
                    debugger;
                    return;
                }
                const endWithBudget = !!this.budgetId;
                // const x = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) * this.todayMonthIndice / chart.data.labels.length
                const ctx = chart.canvas.getContext("2d");
                ctx.save();

                const colWidth =
                    (chart.chartArea.right - chart.chartArea.left) / chart.data.labels.length;
                this.amountColWidth = colWidth;

                //
                // left
                //
                // let y = chart.chartArea.bottom - height
                let point = chart._metasets[0].data[0];
                let y = point.y + point.height;
                let height = this.zeroPos - y;
                ctx.fillStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, 0.15)`;
                // ctx.fillStyle = "#a9c1ce"
                ctx.fillRect(chart.chartArea.left, y, colWidth * 0.5, height);

                ctx.strokeStyle = "#6290aa";
                ctx.lineWidth = 2;

                ctx.beginPath();
                ctx.moveTo(chart.chartArea.left, y);
                ctx.lineTo(chart.chartArea.left + colWidth * 0.5, y);
                point = chart._metasets[1].data[0];
                // const otherY = point.y + point.height
                const otherY = point.y;
                ctx.lineTo(chart.chartArea.left + colWidth * 0.5, otherY);
                ctx.stroke();

                //
                // right
                //

                point = chart._metasets[1].data[chart._metasets[1].data.length - 1];
                y = point.y + point.height;

                height = this.zeroPos - y;
                if (endWithBudget) {
                    ctx.fillStyle = `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, 0.05)`;
                    // ctx.fillStyle = "#d6e0e5"
                }
                ctx.fillRect(chart.chartArea.right - colWidth * 0.5, y, colWidth * 0.5, height);
                if (endWithBudget) {
                    ctx.setLineDash([6, 6]);
                }
                ctx.beginPath();
                ctx.moveTo(chart.chartArea.right - colWidth * 0.5, y);
                ctx.lineTo(chart.chartArea.right, y);
                ctx.stroke();

                ctx.restore();
                //
                //
                //
                this.onUpdateChart(chart);
            },
        };
        return plugin;
    }

    get plugins() {
        return [this.plugin];
    }

    get options() {
        const self = this;
        // const options = ref<ChartOptions<'line'>>({
        return {
            responsive: true,
            plugins: {
                legend: {
                    display: false,
                    position: "top",
                },
                title: {
                    display: false,
                    text: "Plan de trésorerie",
                },
                tooltip: {
                    mode: "index",
                    position: "average",
                    callbacks: {
                        label: function (tooltipItem: any): string | string[] {
                            let label = "";
                            if (
                                tooltipItem.dataset?.data?.length &&
                                tooltipItem.dataIndex < tooltipItem.dataset.data.length
                            ) {
                                const value = tooltipItem.dataset.data[tooltipItem.dataIndex];

                                if (
                                    value &&
                                    value.length &&
                                    tooltipItem.dataIndex > self.todayMonthIndice
                                ) {
                                    label = tooltipItem.dataset
                                        ? self
                                              .$t(
                                                  "dashboard.amountType." +
                                                      tooltipItem.dataset.label +
                                                      "-budget",
                                              )
                                              .toString()
                                        : "";
                                } else {
                                    label = tooltipItem.dataset
                                        ? self
                                              .$t(
                                                  "dashboard.amountType." +
                                                      tooltipItem.dataset.label,
                                              )
                                              .toString()
                                        : "";
                                }
                                if (label) {
                                    label += " : ";
                                }

                                if (value) {
                                    if (value.length) {
                                        label += self.newCommonFormatCurrency(value[1] - value[0]);
                                    } else {
                                        label += self.newCommonFormatCurrency(value);
                                    }
                                } else {
                                    label += tooltipItem.formattedValue;
                                }
                            }
                            return label;
                        },
                    },
                },
            },
            scales: {
                x: {
                    // display: false,
                    grid: {
                        display: false,
                    },
                },
                // y: {
                //     // display: false,
                //     grid:{
                //         display: false
                //     }
                // }
            },
        };
        // });
        // return options
    }

    // const chartData = computed<ChartData<'doughnut'>>(() => ({
    //   labels: ['Paris', 'Nîmes', 'Toulon', 'Perpignan', 'Autre'],
    //   datasets: [
    //     {
    //       data: data.value,
    //       backgroundColor: ['#77CEFF', '#0079AF', '#123E6B', '#97B0C4', '#A5C8ED'],
    //       borderColor: "grey",
    //       borderWidth: 3,
    //       borderRadius: 12,
    //       borderSkipped: false,
    //     },
    //   ],
    // }));

    get months() {
        const months = [];
        if (this.cashFlowPlanData?.cashFlow?.dates) {
            return this.cashFlowPlanData.cashFlow.dates.map((d) => moment(d));
        }
        return months;
    }

    @Watch("months")
    onMonthsChanged() {
        //
        // todayMonthIndice
        //
        const now = moment();
        if (this.budgetId) {
            this.todayMonthIndice = this.updateTodayMonthIndice();
        } else if (this.months.length) {
            const startMonth = this.months[0].startOf("month");
            if (now.isBefore(startMonth)) {
                this.todayMonthIndice = -1;
            } else {
                this.todayMonthIndice = 0;
                let found = false;
                for (const month of this.months) {
                    if (now.isSameOrBefore(month.endOf("month"))) {
                        found = true;
                        break;
                    }
                    this.todayMonthIndice++;
                }
            }
        } else {
            this.todayMonthIndice = 0;
        }
    }

    @Watch("selectedPeriod")
    onSelectedPeriodChanged() {
        this.selectPeriodFromBudget();
        this.skipDetailQuery = false;
        this.skipChartQuery = false;
    }

    @Watch("startDate")
    onStartDateChanged() {
        this.startDate_ = this.startDate;
        this.skipChartQuery = false;
        this.skipDetailQuery = false;
    }

    @Watch("endDate")
    onEndDateChanged() {
        this.endDate_ = this.endDate;
        this.skipChartQuery = false;
        this.skipDetailQuery = false;
    }

    get monthsAsString() {
        const months = this.months.map((m) => m.format("MMM"));
        return months;
    }

    todayMonthIndice_: number = moment().month();

    get todayMonthIndice() {
        if (this.cashFlowPlanDetail) {
            return this.cashFlowPlanDetail.todayMonthIndice;
        }
        return this.todayMonthIndice_;
    }

    set todayMonthIndice(value) {
        if (this.cashFlowPlanDetail) {
            this.cashFlowPlanDetail.todayMonthIndice = value;
        }
        this.todayMonthIndice_ = value;
    }

    updateTodayMonthIndice() {
        const now = moment(); // .month();
        if (!this.startDate_ || moment(this.startDate_).isAfter(now)) {
            this.disableSlider = true;
            return -1;
        }
        this.disableSlider = false;
        let startMonth = 0;
        if (this.startDate_) {
            startMonth = moment(this.startDate_).month();
        }
        return now.month() - startMonth;
    }

    get nowIsOutsidePeriod() {
        if (this.todayMonthIndice < 0 || this.todayMonthIndice >= this.months.length) {
            return true;
        }
        return false;
    }

    get nowIsInsidePeriod() {
        if (this.todayMonthIndice < 0 || this.todayMonthIndice >= this.months.length) {
            return false;
        }
        return true;
    }

    get toDayWidth() {
        if (
            !this.isChartDefined ||
            !gChart ||
            !gChart.chartArea ||
            !gChart.data ||
            this.nowIsOutsidePeriod
        )
            return 0;
        return (gChart.chartArea.width * this.todayMonthIndice) / gChart.data.labels.length;
    }

    get toDayX() {
        if (!this.isChartDefined || !gChart?.chartArea) return 0;
        return gChart.chartArea.left + this.toDayWidth;
    }

    isAfterToday(ctx, value) {
        // debugger
        return ctx.p0.x > this.toDayX ? value : undefined;
    }

    getBackgroundColors(r, g, b) {
        const colors = [];
        const todayMonthIndice = this.nowIsInsidePeriod
            ? this.todayMonthIndice
            : this.months.length;
        for (let index = 0; index < todayMonthIndice; index++) {
            colors.push(`rgba(${r},${g},${b}, 1)`);
        }
        for (let index = todayMonthIndice; index < this.months.length; index++) {
            colors.push(`rgba(${r},${g},${b}, 0.5)`);
        }
        return colors;
    }

    // chartData = computed<ChartData<'line'>>(() => ({
    get chartData() {
        // const self = this
        // debugger
        // const limitValues = this.selectPeriodFromDatasets;
        // const startDate = limitValues.start;
        // const endDate = limitValues.end;
        if (this.cashFlowPlanData) {
            return {
                labels: this.monthsAsString, // .slice(startDate, endDate),
                datasets: [
                    {
                        label: "incomes",
                        data: this.floatingIncomes, //.slice(startDate, endDate),
                        borderColor: "#5FC9B8",
                        backgroundColor: this.getBackgroundColors(95, 201, 104),
                        //borderColor: "grey",
                        // borderWidth: 2,
                        // borderRadius: 6,
                        // borderSkipped: false,
                        type: "bar",
                        order: 1,
                        barThickness: 12,
                    },
                    {
                        label: "expenses",
                        data: this.floatingExpenses, //.slice(startDate, endDate),
                        borderColor: "#EC6D56",
                        backgroundColor: this.getBackgroundColors(236, 109, 86),
                        // borderWidth: 2,
                        // borderRadius: 6,
                        // borderSkipped: false,
                        type: "bar",
                        order: 1,
                        barThickness: 12,
                    },
                    {
                        label: "cashFlow",
                        data: this.cashFlow, //.slice(startDate, endDate),
                        borderColor: `rgba(${this.color.r},${this.color.g},${this.color.b},1)`,
                        // borderColor: "#6290aa",
                        borderWidth: 2,
                        fill: true,
                        backgroundColor: `rgba(${this.color.r},${this.color.g},${this.color.b}, 0.5)`,
                        // backgroundColor: `#a9c1ce`,
                        // backgroundColor: this.getBackgroundColors(249,177,42),
                        // cubicInterpolationMode: 'monotone',
                        // tension: 0.4,
                        stepped: "before",
                        // segment: {
                        //     // borderColor: ctx => self.isAfterToday(ctx, "grey"),
                        //     borderDash: ctx => self.isAfterToday(ctx, [6, 6]),

                        // },
                        order: 0,
                    },
                    {
                        label: "cashFlow-budget",
                        data: this.budgetCashFlow, //.slice(startDate, endDate),
                        borderColor: `rgba(${this.color.r},${this.color.g},${this.color.b},1)`,
                        // borderColor: "#6290aa",
                        borderWidth: 2,
                        fill: true,
                        backgroundColor: `rgba(${this.color.r},${this.color.g},${this.color.b}, 0.2)`,
                        // backgroundColor: `#d6e0e5`,
                        borderDash: [6, 6],
                        // cubicInterpolationMode: 'monotone',
                        // tension: 0.4,
                        stepped: "before",
                        // segment: {
                        // borderColor: ctx => self.isAfterToday(ctx, "grey"),
                        // borderDash: ctx => self.isAfterToday(ctx, [6, 6]),

                        // },
                        order: 0,
                    },
                ],
            };
        }
        return null;
    }

    updateCashFlow() {
        // this.cashFlow = ref([])
        // this.budgetCashFlow = ref([])
        // this.floatingIncomes = ref([])
        // this.floatingExpenses = ref([])
        this.cashFlow = [];
        this.budgetCashFlow = [];
        this.floatingIncomes = [];
        this.floatingExpenses = [];

        // this.cashFlow.value = []
        // this.budgetCashFlow.value = []
        // this.floatingIncomes.value = []
        // this.floatingExpenses.value = []
        if (this.expenses.length) {
            let currentBalance = this.cashFlowPlanData.cashFlow.startBalance;
            const todayMonthIndice = this.nowIsInsidePeriod
                ? this.todayMonthIndice
                : this.months.length;
            for (let index = 0; index < todayMonthIndice; index++) {
                const incomeTop = currentBalance + this.incomes[index];
                this.floatingIncomes.push([currentBalance, incomeTop]);
                this.floatingExpenses.push([incomeTop + this.expenses[index], incomeTop]);
                currentBalance += this.incomes[index] + this.expenses[index];
                this.cashFlow.push(currentBalance);
                this.budgetCashFlow.push(NaN);
            }
            for (let index = todayMonthIndice; index < this.months.length; index++) {
                const incomeTop = currentBalance + this.budgetIncomes[index];
                this.floatingIncomes.push([currentBalance, incomeTop]);
                this.floatingExpenses.push([incomeTop + this.budgetExpenses[index], incomeTop]);
                currentBalance += this.budgetIncomes[index] + this.budgetExpenses[index];
                if (index > todayMonthIndice) {
                    this.cashFlow.push(NaN);
                } else {
                    this.cashFlow.push(currentBalance);
                }
                this.budgetCashFlow.push(currentBalance);
            }
        }
    }

    amountColWidth: number = 0;

    computeAmountColWidth() {
        if (gChart?.data?.labels?.length) {
            if (gChart) {
                const colWidth =
                    (gChart.chartArea.right - gChart.chartArea.left) / gChart.data.labels.length;
                // if (!this.slided && this.budgetId) {
                //     colWidth += 34;
                // }
                if (Math.abs(colWidth - this.amountColWidth) > 1.0e-3) {
                    this.amountColWidth = colWidth;
                }
            }
        }
    }

    // colWidth_ = 0

    // get colWidth() {
    //     if (this.colWidth_) {
    //         return this.colWidth_
    //     }
    //     if (this.isChartDefined && gChart) {
    //         let width = (gChart.chartArea.right - gChart.chartArea.left) / (gChart.data.labels.length)
    //         if (!this.slided && this.budgetId) {
    //             width += 34;
    //         }
    //         // console.log("colWidth = "+ width)
    //         return width;
    //     }
    //     return 0
    // }

    // set colWidth(value) {
    //     this.colWidth_ = value
    // }

    headerStyle(index: number) {
        let style = "";
        if (this.isMonthColumn(index)) {
            const modulo = index % 2;
            const width = modulo ? Math.round(this.amountColWidth) : Math.abs(this.amountColWidth);
            style = this.amountColWidth ? "padding:0; width:" + width + "px !important;" : "";
            style += "cursor:pointer";
        } else {
            style += "cursor:auto";
        }

        return style;
    }

    amountStyle(row: any, mi: number) {
        // if (!gChart) return ""
        // let colWidth = 0
        // if ( gChart?.data?.labels?.length) {
        //     if (mi < gChart.data.labels.length) {
        //         if (this.isChartDefined && gChart) {
        //             colWidth = (gChart.chartArea.right - gChart.chartArea.left) / (gChart.data.labels.length)
        //             if (Math.abs(colWidth - this.colWidth) > 1.0e-3) {
        //                 this.colWidth = colWidth
        //             }
        //         }
        //     } else {
        //         colWidth = this.additionalColWidth
        //     }
        // }
        const modulo = mi % 2;
        const width = modulo ? Math.round(this.amountColWidth) : Math.abs(this.amountColWidth);
        let style = this.amountColWidth ? "padding:0; width:" + width + "px !important;" : "";
        if (mi === this.todayMonthIndice) {
            style += "backgroundColor:" + this.todayBackgroundColor + ";";
        }
        if (row) {
            if (this.isCellEditable(row, mi)) {
                style += "cursor: pointer;";
            }
            if (this.cashFlowPlanDetail.isCustomAmount(row, mi)) {
                style += "color: blue;";
            }
        }
        return style;
    }

    amountClass(row: any, mi: number) {
        let amountClass = "text-center";
        if (this.isCellEditable(row, mi)) {
            amountClass += " amountHover";
        }
        return amountClass;
    }

    getValue(row, index) {
        if (this.cashFlowPlanDetail) {
            const value = this.displaySign * this.cashFlowPlanDetail.getAmount(row, index);
            return newCommonFormatCurrency(value);
        }
        return "XXX";
    }

    //  onDateChanged(date) {
    //     this.date = date;
    // }

    async computeLimitDates() {
        if (this.inBudgetId && this.budget) {
            const study = this.budget.parameters
                ? {
                      firstPeriodMonthsNumber: this.budget.parameters.firstPeriodMonthsNumber,
                      periodsNumber: this.budget.parameters.periodsNumber,
                  }
                : this.budget.form && this.budget.form.study
                  ? this.budget.form.study
                  : null;
            const monthCount = study.firstPeriodMonthsNumber + (study.periodsNumber - 1) * 12 - 1;
            this.startDate_ = this.budget.date;
            this.endDate_ = moment(this.budget.date).add(monthCount, "months").format();
            this.selectPeriodFromBudget();
        } else {
            this.startDate_ = this.startDate;
            this.endDate_ = this.endDate;
        }
        this.skipDetailQuery = false;
        this.skipChartQuery = false;
    }

    emitPeriodsList() {
        if (
            this.budget &&
            ((this.budget.form && this.budget.form.study) || this.budget.parameters)
        ) {
            const periodsNumber = this.budget.parameters
                ? this.budget.parameters.periodsNumber
                : this.budget.form.study.periodsNumber;
            const periods = [];
            const firstLine = { id: 1, name: "1er exercice" };
            periods.unshift(firstLine);
            for (let periodIndex = 2; periodIndex <= periodsNumber; periodIndex++) {
                const newLine = { id: periodIndex, name: periodIndex + "e exercice" };
                periods.push(newLine);
            }
            this.periodsList = periods;
            this.$emit("periods-list-changed", this.periodsList);
        }
    }

    selectPeriodFromBudget() {
        // return start and end parameters to pass to datasets.slice
        // if this computation is to heavy we can store an array with computed indexes and just find the correct return value in this array when selectedPeriod change
        const study = this.budget.parameters
            ? {
                  firstPeriodMonthsNumber: this.budget.parameters.firstPeriodMonthsNumber,
                  periodsNumber: this.budget.parameters.periodsNumber,
              }
            : this.budget.form.study;
        let startValue = moment(this.budget.date);
        const firstMonthCount = study ? study.firstPeriodMonthsNumber - 1 : 11;
        let endValue = moment(this.budget.date).add(firstMonthCount, "months");
        if (study && this.selectedPeriod > 1) {
            startValue = endValue.clone().add(1, "months");
            endValue = endValue.add(12, "months");
            if (this.selectedPeriod > 2) {
                startValue = endValue.clone().add(1, "months");
                endValue = endValue.add(12, "months");
                if (this.selectedPeriod > 3) {
                    startValue = endValue.clone().add(1, "months");
                    endValue = endValue.add(12, "months");
                    if (this.selectedPeriod > 4) {
                        startValue = endValue.clone().add(1, "months");
                        endValue = endValue.add(12, "months");
                    }
                }
            }
        }

        const monthCount = study.firstPeriodMonthsNumber + study.periodsNumber * 12 - 1;
        const budgetEndDate = moment(this.budget.date).add(monthCount, "months");
        if (startValue.isAfter(budgetEndDate)) {
            startValue = budgetEndDate.clone().add(-11, "months");
            endValue = budgetEndDate.clone();
        }
        if (endValue.isAfter(budgetEndDate)) {
            endValue = budgetEndDate.clone();
            startValue = budgetEndDate.clone().add(-11, "months");
        }
        this.startDate_ = startValue.format();
        this.endDate_ = endValue.format();
    }

    selectLinesForExpand(fullList, rootName) {
        const selection = [];
        for (const line of fullList) {
            if (line.name === rootName) {
                selection.push(line);
            }
        }
        return selection;
    }

    expand(item) {
        if (item.expanded !== undefined) {
            item.expanded = !item.expanded;
        }
    }

    displaySign: number = -1;

    get detailTable(): any[] {
        if (!this.cashFlowPlanDetail?.data) {
            return null;
        }
        if (this.cashFlowPlanDetail.data.operatingAccount) {
            return this.cashFlowPlanDetail.data.operatingAccount.rows;
        }
        return this.cashFlowPlanDetail.data.names;
    }

    get totals() {
        let totals = [];
        if (this.cashFlowPlanDetail && this.detailTable?.length) {
            totals = new Array(this.detailTable[0].cashFlow.length).fill(0.0);

            for (const row of this.detailTable) {
                for (let index = 0; index < row.cashFlow.length; index++) {
                    totals[index] +=
                        this.displaySign * this.cashFlowPlanDetail.getAmount(row, index);
                }
            }
        }
        return totals;
    }

    getRowName(row) {
        return row.thirdPartyName || row.displayName || row.name;
    }

    //
    // edition
    //

    isEditionValid: boolean = false;

    defaultCellToEdit = {
        accountingLineId: "",
        row: null,
        mi: 0,
        budgetAmount: 0,
        inLateAmount: 0,
    };

    cellToEdit = _.cloneDeep(this.defaultCellToEdit);

    showEditCellDialog: boolean = false;

    get disableInLateAmountEdition() {
        if (!this.cellToEdit?.row?.balance) {
            return true;
        }
        return false;
    }

    getBalanceDelta(row) {
        return this.cashFlowPlanDetail.getInLateBalance(row);
    }

    get totalInLateAmount() {
        let totalInLateAmount = this.cellToEdit.inLateAmount || 0;
        for (let mi = this.todayMonthIndice; mi < this.cellToEdit.row.budget.length; mi++) {
            if (mi !== this.cellToEdit.mi) {
                totalInLateAmount += this.cashFlowPlanDetail.getInLateAmount(
                    this.cellToEdit.row,
                    mi,
                );
            }
        }
        return totalInLateAmount;
    }

    get inLateAmountRules() {
        return [
            (v) => {
                if (v === 0) return true;
                if (v === null) return true;
                if (this.cellToEdit.row.balance > 0) {
                    if (v < 0) {
                        return "Le montant doit être positif";
                    }

                    if (this.totalInLateAmount > this.cellToEdit.row.balance) {
                        return (
                            "Le montant doit être inférieur à " +
                            Math.round(
                                (this.cellToEdit.row.balance - (this.totalInLateAmount - v)) * 100,
                            ) /
                                100
                        );
                    }
                    return true;
                } else if (this.cellToEdit.row.balance < 0) {
                    if (v > 0) {
                        return "Le montant doit être négatif";
                    }

                    if (this.totalInLateAmount < this.cellToEdit.row.balance) {
                        return (
                            "Le montant doit être supérieur à " +
                            Math.round(
                                (this.cellToEdit.row.balance - (this.totalInLateAmount - v)) * 100,
                            ) /
                                100
                        );
                    }
                    return true;
                }
                return true;
            },
        ];
    }

    get monthCount() {
        return this.chartData?.labels?.length || 0;
    }

    isCellEditable(row: any, colIndex: number) {
        if (row.subRows?.length) {
            return false;
        }

        if (colIndex < this.todayMonthIndice) {
            return false;
        }
        // if (row.accountableAccount && row.tagId) {

        // }
        if (colIndex >= this.monthCount) {
            return false;
        }
        return true;
    }

    openEditCell(row: any, mi: number) {
        if (this.isCellEditable(row, mi)) {
            this.cellToEdit.row = row;
            this.cellToEdit.mi = mi;
            this.cellToEdit.budgetAmount = this.cashFlowPlanDetail.getBudgetAmount(row, mi);
            this.cellToEdit.inLateAmount = this.cashFlowPlanDetail.getInLateAmount(row, mi);
            this.showEditCellDialog = true;
        } else if (row.subRows) {
            this.expand(row);
        }
    }

    async saveEditCell() {
        // if (this.cellToEdit?.row?.displaySign) {
        //     this.cellToEdit.value *= this.cellToEdit.row.displaySign
        // }
        await this.cashFlowPlanDetail.setCustomAmount(this.cellToEdit);
        this.cellToEdit = _.cloneDeep(this.defaultCellToEdit);
        this.refresh();
        this.showEditCellDialog = false;
    }

    async resetCustomAmount() {
        // await this.dashboardTable.resetCustomAmount(this.cellToEdit.row, this.cellToEdit.colKey, this.cellToEdit.pi, this.cellToEdit.mi)
        this.showEditCellDialog = false;
    }

    ifCellToEditAlreadyModified() {
        return this.cashFlowPlanDetail.isCustomAmount(this.cellToEdit.row, this.cellToEdit.mi);
    }

    computeRealAndBudgetValues(cashFlowDataList, subRows = false, nameKey = "name") {
        if (!cashFlowDataList) {
            return [];
        }
        const valuesTable = [];
        for (const value of cashFlowDataList) {
            const monthsValues = [];
            let currentName = null;
            const cashFlowValues = value.cashFlow?.entries();

            if (cashFlowValues) {
                for (const [mi, cashFlowValue] of cashFlowValues) {
                    const budgetAmount = this.cashFlowPlanDetail.getTotalBudgetAmount(value, mi);
                    const diff = cashFlowValue - budgetAmount;
                    currentName = value?.[nameKey];
                    monthsValues.push({
                        name: value?.[nameKey],
                        amount: cashFlowValue,
                        budgetAmount: budgetAmount,
                        diff: diff,
                        percDiffBudget: ((cashFlowValue - budgetAmount) * 100) / budgetAmount,
                    });
                }
            }

            const obj: any = { name: currentName, monthsValues: monthsValues };
            if (subRows && value.subRows) {
                obj.subRows = this.computeRealAndBudgetValues(
                    value.subRows,
                    false,
                    "thirdPartyName",
                );
            }
            valuesTable.push(obj);
        }

        return valuesTable;
    }

    get realAndBudgetValues() {
        return this.computeRealAndBudgetValues(this.detailTable, true);
    }

    computeItemTotalValues(cashFlowDataList) {
        const total = [];
        const totalBudget = [];
        const totalPos = [];
        const totalNeg = [];
        const totalBudgetPos = [];
        const totalBudgetNeg = [];
        const totalDiff = [];

        // let amountTotalNeg = 0
        // let amountTotalPos = 0
        // let budgetAmountTotalNeg = 0
        // let budgetAmountTotalPos = 0
        // for (const value of realAndBudgetValuesMi) {
        //     if (value.value.amount > 0) {
        //         amountTotalPos += value.value.amount
        //     } else {
        //         amountTotalNeg += value.value.amount
        //     }
        //     if (value.value.budgetAmount > 0) {
        //         budgetAmountTotalPos += value.value.budgetAmount
        //     } else {
        //         budgetAmountTotalNeg += value.value.budgetAmount
        //     }
        // }

        for (let i = 0; i < 12; i++) {
            total.push(null);
            totalBudget.push(null);
            totalPos.push(null);
            totalBudgetPos.push(null);
            totalNeg.push(null);
            totalBudgetNeg.push(null);
            totalDiff.push(null);
        }

        for (const value of cashFlowDataList) {
            const cashFlowValues = value.cashFlow?.entries();

            if (cashFlowValues) {
                for (const [mi, cashFlowValue] of cashFlowValues) {
                    const budgetAmount = this.cashFlowPlanDetail.getTotalBudgetAmount(value, mi);
                    const diff = cashFlowValue - budgetAmount;
                    if (cashFlowValue > 0) {
                        totalPos[mi] += cashFlowValue;
                    } else {
                        totalNeg[mi] += cashFlowValue;
                    }

                    if (budgetAmount > 0) {
                        totalBudgetPos[mi] += budgetAmount;
                    } else {
                        totalBudgetNeg[mi] += budgetAmount;
                    }
                    total[mi] += cashFlowValue;
                    totalBudget[mi] += budgetAmount;
                    totalDiff[mi] += diff;
                }
            }
        }

        const out = {
            amountTotals: total,
            budgetTotals: totalBudget,
            amountPosTotals: totalPos,
            budgetPosTotals: totalBudgetPos,
            amountNegTotals: totalNeg,
            budgetNegTotals: totalBudgetNeg,
            diffTotals: totalDiff,
            amountTotal: total.reduce((tot, x) => tot + x, 0),
            budgetTotal: totalBudget.reduce((tot, x) => tot + x, 0),
            amountPosTotal: totalPos.reduce((tot, x) => tot + x, 0),
            budgetPosTotal: totalBudgetPos.reduce((tot, x) => tot + x, 0),
            amountNegTotal: totalNeg.reduce((tot, x) => tot + x, 0),
            budgetNegTotal: totalBudgetNeg.reduce((tot, x) => tot + x, 0),
            diffTotal: totalDiff.reduce((tot, x) => tot + x, 0),
        };
        return out;
    }

    get itemTotalValues() {
        return this.computeItemTotalValues(this.detailTable);
    }

    getRealAndBudgetValuesForMI(mi) {
        const values = this.realAndBudgetValues.map((val) => {
            const totalAmount = this.itemTotalValues.amountTotals[mi];
            const budgetAmount = this.itemTotalValues.budgetTotals[mi];
            const totalAmountPercent = totalAmount
                ? (val.monthsValues[mi].amount * 100) / totalAmount
                : null;
            const budgetAmountPercent = budgetAmount
                ? (val.monthsValues[mi].budgetAmount * 100) / budgetAmount
                : null;
            let relativeAmountPercent = null;
            let relativeBudgetPercent = null;
            if (this.itemTotalValues.amountPosTotals[mi] && val.monthsValues[mi].amount >= 0) {
                relativeAmountPercent =
                    (val.monthsValues[mi].amount * 100) / this.itemTotalValues.amountPosTotals[mi];
            } else if (
                this.itemTotalValues.amountNegTotals[mi] &&
                val.monthsValues[mi].amount <= 0
            ) {
                relativeAmountPercent =
                    (val.monthsValues[mi].amount * 100) / this.itemTotalValues.amountNegTotals[mi];
            }
            if (
                this.itemTotalValues.budgetPosTotals[mi] &&
                val.monthsValues[mi].budgetAmount >= 0
            ) {
                relativeBudgetPercent =
                    (val.monthsValues[mi].budgetAmount * 100) /
                    this.itemTotalValues.budgetPosTotals[mi];
            } else if (
                this.itemTotalValues.budgetNegTotals[mi] &&
                val.monthsValues[mi].budgetAmount <= 0
            ) {
                relativeBudgetPercent =
                    (val.monthsValues[mi].budgetAmount * 100) /
                    this.itemTotalValues.budgetNegTotals[mi];
            }
            return {
                name: val.name,
                ...val.monthsValues[mi],
                totalAmountPercent: totalAmountPercent,
                budgetAmountPercent: budgetAmountPercent,
                relativeAmountPercent: relativeAmountPercent,
                relativeBudgetPercent: relativeBudgetPercent,
            };
            // return {
            //     name: val.name,
            //     value: {...val.monthsValues[mi], totalAmountPercent: totalAmountPercent, budgetAmountPercent: budgetAmountPercent, relativeAmountPercent: relativeAmountPercent, relativeBudgetPercent: relativeBudgetPercent}
            // }
        });
        return values;
    }

    roundNumber(nbr, decimals = 2) {
        if (nbr == null || typeof nbr !== "number") {
            return nbr;
        }
        return +nbr.toFixed(decimals);
    }

    getRealAndBudgetTotalsForMI(mi) {
        const realAndBudgetValuesMi = this.getRealAndBudgetValuesForMI(mi);
        const totals = [];
        let amountTotalNeg = 0;
        let amountTotalPos = 0;
        let budgetAmountTotalNeg = 0;
        let budgetAmountTotalPos = 0;
        for (const value of realAndBudgetValuesMi) {
            if (value.amount > 0) {
                amountTotalPos += value.amount;
            } else {
                amountTotalNeg += value.amount;
            }
            if (value.budgetAmount > 0) {
                budgetAmountTotalPos += value.budgetAmount;
            } else {
                budgetAmountTotalNeg += value.budgetAmount;
            }
        }
        const diffPos = amountTotalPos - budgetAmountTotalPos;
        const diffNeg = amountTotalNeg - budgetAmountTotalNeg;
        const percDiffAmountPos =
            ((amountTotalPos - budgetAmountTotalPos) * 100) / budgetAmountTotalPos;
        const percDiffAmountNeg =
            ((amountTotalNeg - budgetAmountTotalNeg) * 100) / budgetAmountTotalNeg;

        totals.push({
            name: "Total décaissement",
            amount: amountTotalNeg,
            budgetAmount: budgetAmountTotalNeg,
            diff: diffNeg,
            percDiffBudget: percDiffAmountNeg,
        });
        totals.push({
            name: "Encaissement",
            amount: amountTotalPos,
            budgetAmount: budgetAmountTotalPos,
            diff: diffPos,
            percDiffBudget: percDiffAmountPos,
        });
        totals.push({
            name: "Total",
            amount: amountTotalPos + amountTotalNeg,
            budgetAmount: budgetAmountTotalPos + budgetAmountTotalNeg,
            diff: diffPos + diffNeg,
            percDiffBudget:
                ((amountTotalPos + amountTotalNeg - (budgetAmountTotalPos + budgetAmountTotalNeg)) *
                    100) /
                (budgetAmountTotalPos + budgetAmountTotalNeg),
        });
        return totals;
    }

    selectedMonthsHaveDataFor(type: "amount" | "budgetAmount") {
        for (const i in this.selectedMonthsRangeData) {
            for (const obj of this.selectedMonthsRangeData[i].monthValues) {
                if (obj?.[type]) {
                    return true;
                }
            }
        }
        return false;
    }

    get selectedMonthsHaveData() {
        // const type = this.pieChartSwitchValue ? "budgetAmount" : "amount"
        return (
            this.selectedMonthsHaveDataFor("amount") ||
            this.selectedMonthsHaveDataFor("budgetAmount")
        );
    }

    get pieChartOptions() {
        const self = this;
        return {
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                },
                tooltip: {
                    callbacks: {
                        label: function (tooltipItem) {
                            let label = self.newCommonFormatCurrency(tooltipItem.raw) || "";
                            // let value = tooltipItem.dataset.data[tooltipItem.dataIndex] || 0;
                            const data = self.pieChartFullData[tooltipItem.dataIndex];
                            if (self.pieChartSwitchValue && data.relativeBudgetPercent != null) {
                                label +=
                                    " / " + self.roundNumber(data.relativeBudgetPercent, 2) + "%";
                            } else if (
                                !self.pieChartSwitchValue &&
                                data.relativeAmountPercent != null
                            ) {
                                label +=
                                    " / " + self.roundNumber(data.relativeAmountPercent, 2) + "%";
                            }
                            return label;
                        },
                    },
                },
            },
            maintainAspectRatio: false,
            ticks: {
                min: 0,
            },
        };
    }

    openPieChartDialog() {
        this.toggleMonthDataViewType = 0;
        this.distributionDialog = true;
        this.pieChartSwitchValue = false;
        this.toggleDatePicker = 0;
    }

    getSelectedMonthsPieChartData(
        dataType: "amount" | "budgetAmount" | "diff" | "percDiffBudget" = "amount",
    ) {
        let pieChartFullData = [];
        if (this.selectedMonthsInRangeIndices.length === 1) {
            pieChartFullData = this.getRealAndBudgetValuesForMI(
                this.selectedMonthsInRangeIndices[0],
            );
        } else {
            const data = this.selectedMonthsInRangeIndices.map((mi, i) => ({
                dataValues: this.getRealAndBudgetValuesForMI(mi),
                monthName: this.monthsAsString[mi],
            }));
            pieChartFullData = this.formatMonthsRangeData(data).map((x) => ({
                [dataType]: this.sumByKey(x.monthValues, dataType),
                name: x.name,
            }));
        }
        // pieChartFullData.sort((a, b) => Math.abs(b[dataType]) - Math.abs(a[dataType]))
        const labels = pieChartFullData.map((val) => val.name);
        const dataValues = pieChartFullData.map((val) => val[dataType]);
        const pieDatasets = [
            {
                data: dataValues,
            },
        ];
        chartColors("pie", pieDatasets[0], "warm"); // cool, warm, neon
        const pieData = {
            labels: labels,
            datasets: pieDatasets,
        };
        this.pieChartFullData = [...pieChartFullData];
        return pieData;
    }

    get selectedMonthsPieChartData() {
        return this.getSelectedMonthsPieChartData(
            this.pieChartSwitchValue ? "budgetAmount" : "amount",
        );
    }

    pieChartFullData = [];

    get pieChartRefreshKey() {
        return JSON.stringify(this.selectedMonthsPieChartData);
    }

    get allPossibleDateMonths() {
        return this.months.map((x) => moment(x).format("YYYY-MM"));
    }

    onTableHeaderColumnClick(index) {
        debugger;
        // There is an offset of 2 in the indices of the month in the table because the two first columns are not months.
        // If a column is added or removed before the months, the offset should be updated.
        if (this.isMonthColumn(index)) {
            const monthIndex = index - this.columnsOffsetBeforeMonths;
            this.selectedMonths =
                this.allPossibleDateMonths?.slice(monthIndex, monthIndex + 1) ?? [];

            if (!this.selectedMonthsHaveData) {
                this.monthDataSnackbar = true;
                this.monthDataSnackbarMsg = "Il n'y a aucune donnée pour cette période";
            }
            this.openPieChartDialog();
        }
    }

    isMonthColumn(index: number): boolean {
        return (
            index >= this.columnsOffsetBeforeMonths &&
            index <= this.monthCount + this.columnsOffsetBeforeMonths - 1
        );
    }

    get selectedMonthsInRange() {
        if (!this.selectedMonths.length) {
            return [];
        }

        const selectionStart = this.selectedMonths.sort((a, b) => a.localeCompare(b))[0];
        const selectionEnd = this.selectedMonths.sort((a, b) => a.localeCompare(b))[
            this.selectedMonths.length - 1
        ];

        if (moment(selectionStart, "YYYY-MM").isSame(moment(selectionEnd, "YYYY-MM"), "month"))
            return [selectionStart];
        let date = selectionStart;
        const dates = [date];
        do {
            date = moment(date).add(1, "month");
            dates.push(date.format("YYYY-MM"));
        } while (moment(date).isBefore(selectionEnd));
        return dates;
    }

    get selectedMonthsInRangeIndices() {
        const refStart = this.allPossibleDateMonths[0]; //moment(this.startDate_).format("YYYY-MM")
        return this.selectedMonthsInRange.map((date) => moment(date).diff(refStart, "month", true));
    }

    get selectedMonthsInRangeNames() {
        return this.selectedMonthsInRangeIndices.map((mi) => this.monthsAsString[mi]);
    }

    get selectedMonthsRangeData() {
        const data = this.selectedMonthsInRangeIndices.map((mi) => ({
            dataValues: this.getRealAndBudgetValuesForMI(mi) as any,
            monthName: this.monthsAsString[mi],
        }));
        return this.formatMonthsRangeData(data);
    }

    get selectedMonthsRangeTotalsData() {
        const data = this.selectedMonthsInRangeIndices.map((mi) => ({
            dataValues: this.getRealAndBudgetTotalsForMI(mi) as any,
            monthName: this.monthsAsString[mi],
        }));
        return this.formatMonthsRangeData(data);
    }

    formatMonthsRangeData(monthsRangeData) {
        if (!monthsRangeData?.length) return [];
        const out = [];
        for (const index in monthsRangeData[0].dataValues) {
            const row = monthsRangeData[0].dataValues[+index];
            const obj: any = { name: row.name };
            obj.monthValues = this.selectedMonthsInRangeIndices.map(
                (_mi, i) => monthsRangeData[i].dataValues[index],
            ); // mi is the global month index and i is the index in the list
            out.push(obj);
        }
        return out;
    }

    sumByKey(objList: any[], key) {
        if (!objList) return 0;
        if (!Array.isArray(objList) || !key) {
            throw TypeError();
        }
        return objList.reduce((total, x) => total + x?.[key] || 0, 0);
    }
}

export default toNative(CashFlowPlan);
</script>
<style lang="postcss" scoped>
.amountHover {
    &:hover {
        border: solid 1 #111;
    }
}

.green {
    background-color: #5fc9b8;
    color: white;
}

.custom-red {
    background-color: rgb(255, 138, 128);
    color: white;
}

.custom-orange {
    background-color: rgb(255, 167, 38);
    color: white;
}
</style>
